import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { formatDate } from '@frontend/date';
import { theme } from '@frontend/theme';
import { SchedulePulseInsuranceTabProvider } from '../../context';
import { LocationAccordionList } from '../LocationAccordionList';
import { InsuranceTabDateHeader } from './InsuranceTabDateHeader';
import { SelectionFilterContainer } from './SelectionFilterContainer';
import { SingleLocationView } from './SingleLocationView';
import { VerificationAnalysis } from './VerificationAnalysis';

export type SchedulePulseInsuranceTabProps = {
  selectedLocationIds: string[];
  getScopeName: (scopeId: string, type?: 'location' | 'group' | 'org' | undefined) => string;
  defaultAppointmentList: Appointment[] | CalendarEvent[];
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  isMultiLocation?: boolean;
  setShouldRenderInsuranceAnalysis?: (value: boolean) => void;
};

export const SchedulePulseInsuranceTab = ({
  getScopeName,
  selectedLocationIds,
  defaultAppointmentList,
  selectedDate,
  setSelectedDate,
  isMultiLocation,
}: SchedulePulseInsuranceTabProps) => {
  const [shouldRenderInsuranceAnalysis, setShouldRenderInsuranceAnalysis] = useState(false);

  const schedulePulseInsuranceTabDate = useMemo(() => {
    return selectedDate || formatDate(new Date(), 'MM/DD/YYYY');
  }, [selectedDate]);

  return (
    <SchedulePulseInsuranceTabProvider
      defaultAppointmentList={defaultAppointmentList}
      getScopeName={getScopeName}
      selectedDate={schedulePulseInsuranceTabDate}
      setSelectedDate={setSelectedDate}
      selectedLocationIds={selectedLocationIds}
      isMultiLocation={isMultiLocation}
      setShouldRenderInsuranceAnalysis={setShouldRenderInsuranceAnalysis}
    >
      <section css={scheduleInsuranceTabStyles}>
        {shouldRenderInsuranceAnalysis && <VerificationAnalysis />}
        {!shouldRenderInsuranceAnalysis && (
          <>
            <InsuranceTabDateHeader type='add-to-list' />
            <div css={{ padding: theme.spacing(0, 3) }}>
              {selectedLocationIds.length === 1 && <SelectionFilterContainer />}
            </div>
            <LocationAccordionList
              minBodyHeight={400}
              ContentComponent={SingleLocationView}
              isMultiLocation={isMultiLocation}
              style={locationAccordionListStyle(isMultiLocation || false)}
            />
          </>
        )}
      </section>
    </SchedulePulseInsuranceTabProvider>
  );
};

const locationAccordionListStyle = (isMultiLocation: boolean) => {
  return css({ padding: isMultiLocation ? theme.spacing(3) : theme.spacing(0, 3) });
};

const scheduleInsuranceTabStyles = css({
  height: '100%',
});
